

import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/utils";

export function DynamicFavicon() {
  const { settings } = useSettings();
  const faviconUrl = settings.faviconUrl;
  const logoUrl = settings.logoUrl;
  const primaryColor = settings.theme.primaryColor;
  const businessName = settings.business.businessName;

  useEffect(() => {
    // Update theme color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute("content", primaryColor);
    }

    // Update title based on business name
    if (businessName) {
      document.title = `${businessName} - Premium Event Management`;
    }
  }, [primaryColor, businessName]);

  useEffect(() => {
    // Remove existing favicon links first
    const existingLinks = document.querySelectorAll('link[rel*="icon"]');
    existingLinks.forEach((link) => link.remove());

    // Priority: faviconUrl -> logoUrl -> default favicon
    let faviconHref = '';

    if (faviconUrl) {
      // Use custom favicon if available
      faviconHref = getImageUrl(faviconUrl);
    } else if (logoUrl) {
      // Fallback to logo if no favicon
      faviconHref = getImageUrl(logoUrl);
    }

    // If no favicon URL, use default favicon
    if (!faviconHref) {
      faviconHref = '/favicon.png'; // Default fallback
    }

    // Add new favicon links for different sizes
    const faviconSizes = [
      { rel: "icon", type: "image/png", sizes: "16x16", href: faviconHref },
      { rel: "icon", type: "image/png", sizes: "32x32", href: faviconHref },
      { rel: "icon", type: "image/png", sizes: "48x48", href: faviconHref },
      { rel: "apple-touch-icon", sizes: "180x180", href: faviconHref },
      { rel: "icon", type: "image/png", sizes: "192x192", href: faviconHref },
      { rel: "icon", type: "image/png", sizes: "512x512", href: faviconHref },
      { rel: "shortcut icon", href: faviconHref },
    ];

    faviconSizes.forEach(({ rel, type, sizes, href }) => {
      const link = document.createElement("link");
      link.rel = rel;
      if (type) link.type = type;
      if (sizes) link.sizes = sizes;
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      // Cleanup: remove favicon links when component unmounts or faviconUrl changes
      const links = document.querySelectorAll('link[rel*="icon"]');
      links.forEach((link) => link.remove());
    };
  }, [faviconUrl, logoUrl]);

  return null;
}
